<template>
  <v-card class="pa-3">
    <v-card-title class="headline pl-0 pt-0 pb-2">
      {{
        isBulk
          ? "Change Routes"
          : singleHasRoute
          ? "Change Route"
          : "Assign Route"
      }}
    </v-card-title>

    <div class="mb-3 font-weight-regular grey--text">
      {{
        isBulk
          ? "Select a new route for each absent below, or choose \u201CDetach From Route\u201D to remove its route assignment. Leave blank to make no change."
          : singleHasRoute
          ? "Select a new route below, or choose \u201CDetach From Route\u201D to remove the route assignment."
          : "Select a route to assign below."
      }}
    </div>

    <v-card flat class="mb-3 change-route-search-bar">
      <v-layout row wrap align-center class="pa-3">
        <v-icon color="white" class="mr-2">mdi-routes</v-icon>
        <div class="white--text subheading font-weight-medium mr-4">
          Route Search
        </div>
        <v-select
          class="mr-2 mt-0 pt-0 change-route-search-field"
          style="max-width: 220px; min-width: 220px;"
          v-model="kind"
          label="Kind"
          :items="kindOptions"
          item-text="text"
          item-value="value"
          hide-details
          solo
          flat
          @change="onKindOrTermChanged"
        ></v-select>
        <v-spacer></v-spacer>
        <v-tooltip left max-width="260">
          <template v-slot:activator="{ on }">
            <v-icon color="white" v-on="on">mdi-information-outline</v-icon>
          </template>
          <span
            >Pick Regular or Day Camp to assign a route that is active on
            that absent date.</span
          >
        </v-tooltip>
      </v-layout>
    </v-card>

    <v-layout row class="mb-2" v-if="isBulk">
      <v-spacer></v-spacer>
      <v-btn small flat color="error" @click="setAllToDetach"
        >Detach All</v-btn
      >
      <v-btn small flat color="default" @click="clearAllSelections"
        >Clear Selections</v-btn
      >
    </v-layout>

    <div style="max-height: 460px; overflow-y: auto;" class="px-1">
      <div
        v-for="(row, index) in rows"
        :key="row.id"
        class="pa-2 mb-2 change-route-row"
        v-bind:class="{
          'change-route-row-success': row.resultSuccess === true,
          'change-route-row-error': row.resultSuccess === false,
        }"
      >
        <v-layout row wrap align-center>
          <div style="min-width: 260px;" class="mr-3">
            <div class="font-weight-regular">{{ row.date | date }}</div>
            <div class="font-weight-medium">{{ row.driverLabel }}</div>
            <div
              class="caption"
              v-bind:class="{ 'grey--text': !row.hasRoute }"
            >
              {{ row.currentRouteLabel }}
            </div>
          </div>

          <v-autocomplete
            style="min-width: 320px; max-width: 420px;"
            class="mr-2 mt-0 pt-0"
            v-model="row.selectedRouteOption"
            :items="getRowItems(row)"
            :loading="row.loadingRoutes"
            :disabled="row.loadingRoutes"
            label="New Route"
            hide-details
            clearable
            box
          >
            <template v-slot:item="data">
              <v-list-tile-content>
                <v-list-tile-title
                  v-bind:class="{
                    'error--text font-weight-medium': data.item === DETACH_LABEL,
                  }"
                >
                  {{ data.item }}
                </v-list-tile-title>
              </v-list-tile-content>
            </template>
          </v-autocomplete>

          <v-chip
            v-if="row.resultSuccess !== null"
            small
            v-bind:color="row.resultSuccess ? 'success' : 'error'"
            text-color="white"
          >
            {{ row.resultSuccess ? "Updated" : "Failed" }}
          </v-chip>
        </v-layout>

        <div
          v-if="row.resultSuccess === false"
          class="error--text caption mt-1"
        >
          {{ row.resultMessage }}
        </div>
      </div>

      <div
        v-if="!rows.length"
        class="text-xs-center font-weight-light py-4 grey--text"
      >
        No absent records selected
      </div>
    </div>

    <v-card-actions class="mx-0 px-2 pb-2 mt-2">
      <v-spacer></v-spacer>
      <v-btn color="default" dark @click="cancel">Close</v-btn>
      <v-btn :loading="processing" color="success" @click="save">
        Save
      </v-btn>
    </v-card-actions>

    <deduction-impact-dialog
      v-model="payDialog"
      title="Pay will change"
      intro="Moving or detaching these absences changes the deduction on the days below. Nothing is saved until you confirm."
      confirm-text="Save"
      :loading="payImpactLoading"
      :confirm-loading="processing"
      :days="payImpactDays"
      @confirm="commit"
    ></deduction-impact-dialog>
  </v-card>
</template>

<script>
import moment from "moment";
import { getStoredRouteKind, setStoredRouteKind } from "../../utils/helpers.js";
import deductionImpactDialog from "./components/DeductionImpactDialog.vue";

const DETACH_LABEL = "Detach From Route";

export default {
  components: {
    deductionImpactDialog,
  },
  props: {
    selected: {
      type: Array,
      default: () => [],
    },
    reload: {
      type: Function,
      default: () => {},
    },
    close: {
      type: Function,
      default: () => {},
    },
    onAllSaved: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      DETACH_LABEL,
      processing: false,
      payDialog: false,
      payImpactLoading: false,
      payImpactDays: [],
      pendingChanges: [],
      pendingDetaches: [],

      season: "",
      seasons: [],
      kind: "Regular",
      kindOptions: [
        { text: "Regular", value: "Regular" },
        { text: "Day Camp", value: "DayCamp" },
      ],
      term: "",
      terms: [],
      defaultRegID: "",

      rows: [],
    };
  },
  computed: {
    isBulk() {
      return this.selected && this.selected.length > 1;
    },
    singleHasRoute() {
      const first = this.selected && this.selected[0];
      return !!(first && first.route_ID);
    },
  },
  watch: {
    selected() {
      this.initKindTerm();
      this.buildRows();
    },
  },
  async created() {
    await Promise.all([this.loadTerms(), this.loadRegID()]);

    this.initKindTerm();
    this.buildRows();
  },
  methods: {
    async loadTerms() {
      this.terms = await this.$http.get("api/allStudents/getRegTerms");
    },
    async loadRegID() {
      this.defaultRegID = await this.$http.get("api/allStudents/getRegID");
    },
    initKindTerm() {
      const first = this.selected && this.selected[0];

      this.kind = getStoredRouteKind();
      this.term =
        (first && first.term) || this.defaultRegID || this.terms[0] || "";
    },
    buildRows() {
      this.rows = (this.selected || []).map((absent) => ({
        absent,
        id: absent.id,
        date: absent.date,
        driver_Bus_ID: absent.driver_Bus_ID,
        driverLabel:
          (absent.driver || "") +
          (absent.busNumber ? " (" + absent.busNumber + ")" : ""),
        hasRoute: !!absent.route_ID,
        currentRouteLabel: absent.route_ID
          ? [absent.timeStart, absent.routeIdentifier, absent.groupBy]
              .filter(Boolean)
              .join(" - ")
          : "No Route Assigned",
        routes: [],
        loadingRoutes: false,
        selectedRouteOption: "",
        resultSuccess: null,
        resultMessage: "",
      }));

      this.loadAllRouteOptions();
    },
    onKindOrTermChanged() {
      setStoredRouteKind(this.kind);
      this.loadAllRouteOptions();
    },
    async loadAllRouteOptions() {
      const rowsToLoad = this.rows.filter(
        (row) => row.driver_Bus_ID && row.date
      );

      if (!rowsToLoad.length) return;

      rowsToLoad.forEach((row) => {
        row.loadingRoutes = true;
      });

      try {
        const response = await this.$http.post(
          "api/driverAbsent/getRoutesForDrivers",
          {
            kind: this.kind,
            requests: rowsToLoad.map((row) => ({
              key: row.id,
              driverID: row.driver_Bus_ID,
              date: moment(row.date).format("YYYY-MM-DD"),
            })),
          }
        );

        response.forEach((result) => {
          const row = rowsToLoad.find((r) => r.id == result.key);

          if (!row) return;

          row.routes = (result.routes || []).map((route) => {
            route.displayName =
              route.timeStart +
              " - " +
              route.routeIdentifier +
              " - " +
              route.groupBy;

            return route;
          });
        });
      } catch (e) {
        rowsToLoad.forEach((row) => {
          row.routes = [];
        });
      } finally {
        rowsToLoad.forEach((row) => {
          row.loadingRoutes = false;
        });
      }
    },
    getRowItems(row) {
      return [DETACH_LABEL, ...row.routes.map((r) => r.displayName)];
    },
    setAllToDetach() {
      this.rows.forEach((row) => {
        row.selectedRouteOption = DETACH_LABEL;
      });
    },
    clearAllSelections() {
      this.rows.forEach((row) => {
        row.selectedRouteOption = "";
      });
    },
    getFailureDetail(result) {
      if (!result.existingRecord) return result.message;

      const er = result.existingRecord;
      const details = [
        er.driver,
        er.busNumber ? "Bus #" + er.busNumber : "",
        er.timeStart,
        er.groupBy,
      ]
        .filter(Boolean)
        .join(" \u2022 ");

      return details
        ? result.message + " \u2014 Already assigned to: " + details
        : result.message;
    },
    async save() {
      const changeItems = [];
      const detachIDs = [];

      this.rows.forEach((row) => {
        if (!row.selectedRouteOption) return;

        if (row.selectedRouteOption === DETACH_LABEL) {
          detachIDs.push(row.id);
          return;
        }

        const route = row.routes.find(
          (r) => r.displayName == row.selectedRouteOption
        );

        if (route) {
          changeItems.push({ AbsentID: row.id, RouteID: route.id });
        }
      });

      if (!changeItems.length && !detachIDs.length) {
        return this.$notify({
          group: "notification",
          text:
            "Please select a new route or Detach From Route for at least one record",
          type: "error",
        });
      }

      this.pendingChanges = changeItems;
      this.pendingDetaches = detachIDs;

      const mutations = changeItems
        .map((item) => ({
          absentID: item.AbsentID,
          action: "route",
          routeID: item.RouteID,
        }))
        .concat(
          detachIDs.map((id) => ({
            absentID: id,
            action: "detach",
          }))
        );

      this.payImpactDays = [];
      this.payDialog = true;
      this.payImpactLoading = true;

      try {
        this.payImpactDays =
          (await this.$http.post(
            "api/driverAbsent/previewPayImpact",
            mutations
          )) || [];
      } catch (e) {
        this.payImpactDays = [];
        this.$notify({
          group: "notification",
          text: "Could not check how pay will change",
          type: "error",
        });
      } finally {
        this.payImpactLoading = false;
      }
    },
    async commit(decisions) {
      try {
        this.processing = true;

        const changeResponse = await this.$http.post(
          "api/driverAbsent/updateAbsenceRoutes",
          {
            changes: changeItems,
            detachIds: detachIDs,
            decisions: decisions || [],
          }
        );

        this.processing = false;
        this.payDialog = false;

        const results = (changeResponse && changeResponse.results) || [];
        const failures = results.filter((r) => !r.success);
        const successCount = results.filter((r) => r.success).length;

        results.forEach((result) => {
          const row = this.rows.find((r) => r.id == result.absentID);

          if (!row) return;

          row.resultSuccess = result.success;
          row.resultMessage = result.success
            ? ""
            : this.getFailureDetail(result);

          if (result.success) {
            row.selectedRouteOption = "";
          }
        });

        if (failures.length) {
          this.$notify({
            group: "notification",
            text: `${successCount} updated, ${failures.length} could not be changed`,
            type: "warning",
          });

          this.reload();
        } else {
          this.$notify({
            group: "notification",
            text: "Changes Saved",
            type: "success",
          });

          this.reload();
          this.close();
          this.onAllSaved();
        }
      } catch (e) {
        console.log(e);
        this.processing = false;
        this.$notify({
          group: "notification",
          text:
            (e && e.body && e.body.message) ||
            "Changes were not saved. Check the pay choices and try again.",
          type: "error",
        });
      }
    },
    cancel() {
      this.close();
    },
  },
};
</script>
<style>
.change-route-search-bar {
  background: linear-gradient(135deg, #66bb6a 0%, #4caf50 100%);
  border-radius: 8px;
}
.change-route-search-field .v-input__control .v-input__slot {
  background-color: #fff !important;
  border-radius: 6px !important;
  min-height: 40px;
  padding: 0 10px !important;
  flex-wrap: nowrap;
}
.change-route-search-field .v-label {
  font-size: 12px;
  white-space: nowrap;
}
.change-route-search-field .v-select__selections {
  flex-wrap: nowrap;
}
.change-route-search-field .v-select__selection {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}
.change-route-row {
  border: 1px solid #e0e0e0;
  border-radius: 5px;
}
.change-route-row-success {
  border-color: #5cb860;
  background-color: #f2faf2;
}
.change-route-row-error {
  border-color: #ff5252;
  background-color: #fef4f4;
}
</style>
